import { t, Trans } from "@lingui/macro";
import { CreditCard, Sparkle, UserPlus } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@stylingcv/ui";
import { useLocation, useNavigate } from "react-router-dom";

import { RESUMES_KEY } from "@/client/constants/query-keys";
import { useGuestResume } from "@/client/hooks/use-guest-resume";
import { queryClient } from "@/client/libs/query-client";
import { useResumeStore } from "@/client/stores/resume";
import { AuthRedirection } from "@/client/utils/auth-redirection";
import { createLocalizedNavigation } from "@/client/utils/url-locales";

import { SignupForm } from "./signup-form";

type PurchaseIntent = {
  type: "tokens" | "subscription";
  priceId: string;
  productName: string;
  price: string;
};

type SignupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** When set, shows purchase context and redirects to payment page after signup */
  purchaseIntent?: PurchaseIntent | null;
  /** For guest-to-registered resume transfer (builder context) */
  transferGuestResume?: boolean;
  prefilledEmail?: string;
};

export const SignupModal = ({
  isOpen,
  onClose,
  purchaseIntent,
  transferGuestResume = false,
  prefilledEmail = "",
}: SignupModalProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getLocalizedPath } = createLocalizedNavigation(location.pathname);
  const { saveToLocalStorage } = useGuestResume();

  const handleSuccess = async () => {
    // Save guest resume before any navigation
    if (transferGuestResume) {
      try {
        const currentResumeState = useResumeStore.getState().resume;
        if (currentResumeState.data) {
          localStorage.setItem("guest-resume", JSON.stringify(currentResumeState.data));
        }
        await saveToLocalStorage();

        const freshResume = useResumeStore.getState().resume;
        const completeResumeData = {
          basics: freshResume.data?.basics || {},
          sections: freshResume.data?.sections || {},
          metadata: freshResume.data?.metadata || {},
        };

        const { importResume } = await import("@/client/services/resume/import");
        const newResume = await importResume({
          title: freshResume.title || t`My Resume`,
          slug: "my-resume",
          visibility: "private" as const,
          data: completeResumeData,
        });

        localStorage.removeItem("guest-resume");
        localStorage.removeItem("resume-transition-data");

        // Invalidate resumes cache so dashboard shows the imported resume
        await queryClient.invalidateQueries({ queryKey: RESUMES_KEY });

        onClose();
        navigate(getLocalizedPath(`/builder/${newResume.id}`), { replace: true });
        return;
      } catch (error) {
        console.error("[SignupModal] Failed to transfer guest resume:", error);
        // Fall through to default redirect
      }
    }

    // Purchase flow: close modal, redirect to pricing page
    if (purchaseIntent) {
      sessionStorage.setItem("purchaseIntent", JSON.stringify(purchaseIntent));
      onClose();

      const paymentParam = purchaseIntent.type === "subscription"
        ? `payment=${purchaseIntent.priceId}`
        : `tokens=${purchaseIntent.priceId}`;
      navigate(getLocalizedPath(`/pricing?${paymentParam}`), { replace: true });
      return;
    }

    // Default: redirect to dashboard
    onClose();
    const returnPath = AuthRedirection.resolveReturnPath("/dashboard/resumes");
    navigate(getLocalizedPath(returnPath), { replace: true });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="gap-0 p-0 sm:max-w-[440px]">
        <div className="mx-auto w-[85%] pb-2 pt-8 text-center">
          {purchaseIntent ? (
            <>
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                {t`Create Account to Continue`}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t`Sign up to complete your ${purchaseIntent.productName} purchase`}
              </p>
              <div className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800 dark:bg-blue-900/20">
                {purchaseIntent.type === "tokens" ? (
                  <Sparkle className="size-4 text-blue-600 dark:text-blue-400" />
                ) : (
                  <CreditCard className="size-4 text-blue-600 dark:text-blue-400" />
                )}
                <span className="text-sm font-medium">{purchaseIntent.productName}</span>
                <span className="text-xs text-gray-500">{purchaseIntent.price}</span>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                <Trans>Create Your <span className="text-teal-600 dark:text-teal-400">Free</span> Account</Trans>
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t`Save your resume and unlock all features`}
              </p>
            </>
          )}
        </div>

        <div className="mx-auto w-[65%] pb-8 pt-4">
          <SignupForm
            onSuccess={handleSuccess}
            prefilledEmail={prefilledEmail}
            showSocialAuth
            showFooterLinks
            showTrustBadge
            submitLabel={purchaseIntent ? t`Create Account` : undefined}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
